import './player.scss';
import '../playerShared/playerComponents.scss';

import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { PlayerAdapter } from '../playerAdapter';
import { PlayerState } from './playerState';
import { PlayerMasks } from './components';
import { PlayerChromeBottom } from './components/playerChromeBottom';
import { PlayerChromeTop } from './components/playerChromeTop';
import { UserPlayerPoster } from './components/playerPoster';
import { PlayerLiveWaitingScreen } from './components/playerLiveWaitingScreen';
import { PlayerLiveEndedScreen } from './components/playerLiveEndedScreen';
import { PlayerChromeLeft } from './components/playerChromeLeft';
import { PlayerReactionAnimations } from '../playerReactionAnimations/playerReactionAnimations';
import { PlayerLiveNotStreamingScreen } from './components/playerLiveNotStreamingScreen';
import { PlayerAdsAdapter } from '../playerAdsAdapter';
import { PlayerViewMode } from './playerViewMode';
import { AudioPlayerMode } from './components/audioPlayerMode';
import { CliprSpinner } from '../loader';

type Props = {
  model: PlayerState,
  viewMode?: PlayerViewMode,
}

export const Player = observer((props: Props) => {
  const { model, viewMode = 'video' } = props;
  const {
    selector,
    job,
    showLiveWaitingScreen,
    showLiveEndedScreen,
    showLiveNotStreamingScreen
  } = model;
  const chrome = model.chrome;
  const widget = model.store.widget;

  let chromeVis = chrome.visibility;
  if (model.isEditMode)
    chromeVis = 'Visible';

  const className = classNames('user-player', {
    'playing': !model.isPaused,
    'paused': model.isPaused,
    'audio-view-mode': viewMode === 'audio',
    'video-mode': selector?.isVideoModeEnabled,
    'topics-mode': selector?.mode === 'Topics',
    'speakers-mode': selector?.mode === 'Speakers',
    'edit-mode': model.isEditMode,
    'chrome-visible': chromeVis === 'Visible',
    'chrome-hidden': chromeVis === 'Hidden',
    'chrome-compact': chromeVis === 'Compact',
    'chrome-hover': chrome.isHovering,
    'video-hover': chrome.isVideoHovering,
    'region-hover': chrome.isRegionHovering,
    'show-marker-bar': model.itemSource.markerBarItems.length > 0,
    'show-status-bar': widget.isWidgetMode,
    'optional-explorer': model.frameset?.isSectionVisible('Index') && !model.isFullscreen,
    'live-waiting': job?.isLiveStreamWaiting,
    'live-active': job?.isLiveStreaming,
    'live-ended': job?.isLiveStreamProcessing,
    'ads-active': model.isAdsAdapterActive
  });

  useEffect(() => {
    model.mounted();
    return () =>
      model.unmounted();
  }, [model]);

  const showPlayer = !showLiveWaitingScreen && !showLiveEndedScreen && !showLiveNotStreamingScreen;
  const showMiniVideo = viewMode === 'audio' && !!job && !job.isAudioSource;
  const mediaElement = (
    <div id="user-player-video"
      className={classNames({
        'audio-player-inline-video': showMiniVideo,
        'audio-player-hidden-media': viewMode === 'audio' && !showMiniVideo
      })}
      onClick={chrome.handleVideoClick}
      onDoubleClick={chrome.handleVideoDoubleClick}
      onPointerEnter={chrome.handleVideoPointerEnter}
      onPointerLeave={chrome.handleVideoPointerLeave}>

      <PlayerAdapter
        model={model.adapter} />
      <PlayerAdsAdapter
        model={model.adsAdapter} />
    </div>
  );

  return (
    <div id="user-player"
      data-guided-training-id="player-training-video"
      data-fullscreen-mode={model.isFullscreen}
      className={className}
      onPointerMove={chrome.handlePointerMove}
      onTouchStart={chrome.handleTouchStart}
      ref={model.componentRef.ref}
      data-test-ad-tag-url={model.adsAdapter.adTagUrl}>

      {showLiveWaitingScreen &&
        <PlayerLiveWaitingScreen
          model={model} />}

      {showLiveEndedScreen &&
        <PlayerLiveEndedScreen
          job={job}
          model={model} />}

      {showLiveNotStreamingScreen &&
        !showLiveEndedScreen &&
        !showLiveWaitingScreen &&
        <PlayerLiveNotStreamingScreen
          model={model} />}

      {showPlayer &&
        <>
          {viewMode === 'audio' ? (
            <AudioPlayerMode
              model={model}
              mediaSlot={mediaElement}
              showInlineVideo={showMiniVideo} />
          ) : (
            <>
              {mediaElement}

              <UserPlayerPoster
                model={model} />

              <PlayerMasks
                model={model} />

              <div id="user-player-gradient-top" />

              {model.showLiveReactions &&
                <PlayerReactionAnimations
                  model={model.reactionAnimations} />}

              <PlayerChromeTop
                model={model} />

              {
                model.isLiveStream &&
                !model.suppressLiveReactions &&
                <PlayerChromeLeft
                  model={model} />}

              <div id="user-player-gradient-bottom" />
              <PlayerChromeBottom
                model={model} />
            </>
          )}
        </>
      }

      {model.isGeneratingDualPlusReport && (
        <div className="dual-plus-generating-indicator" role="status" aria-live="polite">
          <CliprSpinner className="dual-plus-generating-spinner" />
          <span>Generating Dual+ report</span>
        </div>
      )}
    </div>
  );
});
